import { useState } from 'react';
import {
  Button,
  Group,
  Modal,
  MultiSelect,
  Radio,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Textarea,
  rem,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Dropzone } from '@mantine/dropzone';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { PiUploadLight, PiXLight } from 'react-icons/pi';
import { VscFileMedia } from 'react-icons/vsc';
import { JURISDICTION_LIST } from '@/lib/constants';
import { useCreateLegalRequest, type LegalRequestType } from '@/models/offers';

const JURISDICTION_DATA = JURISDICTION_LIST.map((j) => ({
  value: j.title,
  label: j.title,
}));

const REQUEST_TYPE_TITLES: Record<LegalRequestType, string> = {
  legal_advice: 'Legal Advice Request',
  nda: 'NDA Request',
  evaluation_lo: 'Evaluation LO Request',
  contract_review: 'Contract Review / Draft',
};

const ROLE_OPTIONS = ['Intermediary', 'Agent', 'Introducer', 'End Client'];

export default function LegalRequestCreateModal({
  opened,
  onClose,
  requestType,
  offerId,
}: {
  opened: boolean;
  onClose: () => void;
  requestType: LegalRequestType | null;
  offerId: number;
}) {
  const createMutation = useCreateLegalRequest();
  const [files, setFiles] = useState<File[]>([]);

  const form = useForm({
    initialValues: {
      inquiry: '',
      jurisdictions: [] as string[],
      clientRequest: '',
      additionalInfo: '',
      preliminaryDetails: '',
      background: '',
      role: '',
      entityType: 'Legal Entity',
      signerName: '',
      signerEmail: '',
      entityName: '',
      registryCode: '',
      registeredAddress: '',
      countryOfIncorporation: '',
      fullName: '',
      passportNumber: '',
      dateOfBirth: null as string | null,
      nationality: '',
      email: '',
      residenceAddress: '',
      soleProprietorRegNo: '',
      contractSource: 'Client Contract',
      opportunityNumber: '',
      productName: '',
      otherInfo: '',
      hoursPerMonth: '',
      commenceDate: '',
      clientPhone: '',
      designatedOfficer: '',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.inquiry.trim()) errors.inquiry = 'Required';
      if (values.jurisdictions.length === 0) errors.jurisdictions = 'Required';

      const isLegal =
        requestType === 'legal_advice' || requestType === 'evaluation_lo';
      const hasEntity =
        requestType === 'nda' || requestType === 'contract_review';

      if (isLegal && !values.clientRequest.trim())
        errors.clientRequest = 'Required';
      if (hasEntity && !values.background.trim())
        errors.background = 'Required';

      if (requestType === 'nda' && !values.role) errors.role = 'Required';

      if (hasEntity) {
        if (values.entityType === 'Legal Entity') {
          if (!values.signerName.trim()) errors.signerName = 'Required';
          if (!values.signerEmail.trim()) errors.signerEmail = 'Required';
          if (!values.entityName.trim()) errors.entityName = 'Required';
          if (!values.registryCode.trim()) errors.registryCode = 'Required';
          if (!values.registeredAddress.trim())
            errors.registeredAddress = 'Required';
        } else {
          if (!values.fullName.trim()) errors.fullName = 'Required';
          if (!values.passportNumber.trim()) errors.passportNumber = 'Required';
          if (!values.dateOfBirth) errors.dateOfBirth = 'Required';
          if (!values.nationality.trim()) errors.nationality = 'Required';
          if (!values.email.trim()) errors.email = 'Required';
          if (!values.residenceAddress.trim())
            errors.residenceAddress = 'Required';
        }
      }

      if (requestType === 'contract_review') {
        if (values.contractSource === 'Client Contract') {
          if (!values.opportunityNumber.trim())
            errors.opportunityNumber = 'Required';
          if (!values.productName.trim()) errors.productName = 'Required';
        }
      }

      return errors;
    },
  });

  const handleClose = () => {
    form.reset();
    setFiles([]);
    onClose();
  };

  const handleSubmit = form.onSubmit((values) => {
    const { inquiry, jurisdictions, ...rest } = values;

    const details: Record<string, unknown> = {};
    if (requestType === 'legal_advice' || requestType === 'evaluation_lo') {
      details.clientRequest = rest.clientRequest;
      details.additionalInfo = rest.additionalInfo;
      details.preliminaryDetails = rest.preliminaryDetails;
    }
    if (requestType === 'nda') {
      details.background = rest.background;
      details.role = rest.role;
      details.entityType = rest.entityType;
      if (rest.entityType === 'Legal Entity') {
        details.signerName = rest.signerName;
        details.signerEmail = rest.signerEmail;
        details.entityName = rest.entityName;
        details.registryCode = rest.registryCode;
        details.registeredAddress = rest.registeredAddress;
        details.countryOfIncorporation = rest.countryOfIncorporation;
      } else {
        details.fullName = rest.fullName;
        details.passportNumber = rest.passportNumber;
        details.dateOfBirth = rest.dateOfBirth ?? '';
        details.nationality = rest.nationality;
        details.email = rest.email;
        details.residenceAddress = rest.residenceAddress;
        details.soleProprietorRegNo = rest.soleProprietorRegNo;
      }
    }
    if (requestType === 'contract_review') {
      details.background = rest.background;
      details.entityType = rest.entityType;
      if (rest.entityType === 'Legal Entity') {
        details.signerName = rest.signerName;
        details.signerEmail = rest.signerEmail;
        details.entityName = rest.entityName;
        details.registryCode = rest.registryCode;
        details.registeredAddress = rest.registeredAddress;
        details.countryOfIncorporation = rest.countryOfIncorporation;
      } else {
        details.fullName = rest.fullName;
        details.passportNumber = rest.passportNumber;
        details.dateOfBirth = rest.dateOfBirth ?? '';
        details.nationality = rest.nationality;
        details.email = rest.email;
        details.residenceAddress = rest.residenceAddress;
        details.soleProprietorRegNo = rest.soleProprietorRegNo;
      }
      details.contractSource = rest.contractSource;
      if (rest.contractSource === 'Client Contract') {
        details.opportunityNumber = rest.opportunityNumber;
        details.productName = rest.productName;
        details.otherInfo = rest.otherInfo;
      } else {
        details.hoursPerMonth = rest.hoursPerMonth;
        details.commenceDate = rest.commenceDate;
        details.clientPhone = rest.clientPhone;
        details.designatedOfficer = rest.designatedOfficer;
      }
    }

    createMutation.mutate(
      {
        offerId,
        requestType: requestType!,
        inquiry,
        jurisdictions,
        details,
        files: files.length > 0 ? files : undefined,
      },
      {
        onSuccess: () => {
          handleClose();
          notifications.show({
            message: 'Legal request created',
            color: 'green',
          });
        },
        onError: (err: any) => {
          notifications.show({
            message: err?.message || 'Failed to create request',
            color: 'red',
          });
        },
      },
    );
  });

  if (!requestType) return null;

  const isLegalOrEval =
    requestType === 'legal_advice' || requestType === 'evaluation_lo';
  const hasEntity = requestType === 'nda' || requestType === 'contract_review';
  const isLegalEntity = form.values.entityType === 'Legal Entity';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={REQUEST_TYPE_TITLES[requestType]}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <Stack>
          <Textarea
            label="Client's Inquiry Complexity"
            autosize
            minRows={2}
            required
            {...form.getInputProps('inquiry')}
          />
          <MultiSelect
            label="Jurisdictions"
            data={JURISDICTION_DATA}
            searchable
            clearable
            required
            {...form.getInputProps('jurisdictions')}
          />

          {isLegalOrEval && (
            <>
              <Textarea
                label="Client's exact request and expectations"
                autosize
                minRows={2}
                required
                {...form.getInputProps('clientRequest')}
              />
              <Textarea
                label="Additional info from calls/chats"
                autosize
                minRows={2}
                {...form.getInputProps('additionalInfo')}
              />
              <Textarea
                label="Preliminary info shared with client"
                placeholder="If any preliminary information was shared, describe it here"
                autosize
                minRows={2}
                {...form.getInputProps('preliminaryDetails')}
              />
            </>
          )}

          {requestType === 'nda' && (
            <>
              <Textarea
                label="Background (why NDA needed)"
                autosize
                minRows={2}
                required
                {...form.getInputProps('background')}
              />
              <Radio.Group
                label="Lead/Client role"
                required
                {...form.getInputProps('role')}
              >
                <Group mt="xs">
                  {ROLE_OPTIONS.map((r) => (
                    <Radio key={r} value={r} label={r} />
                  ))}
                </Group>
              </Radio.Group>
            </>
          )}

          {requestType === 'contract_review' && (
            <Textarea
              label="Background"
              autosize
              minRows={2}
              required
              {...form.getInputProps('background')}
            />
          )}

          {hasEntity && (
            <>
              <SegmentedControl
                data={['Legal Entity', 'Natural Person']}
                {...form.getInputProps('entityType')}
              />

              {isLegalEntity ? (
                <>
                  <TextInput
                    label="Full name and title of signer"
                    required
                    {...form.getInputProps('signerName')}
                  />
                  <TextInput
                    label="Email of signer"
                    type="email"
                    required
                    {...form.getInputProps('signerEmail')}
                  />
                  <TextInput
                    label="Full name of legal entity"
                    required
                    {...form.getInputProps('entityName')}
                  />
                  <TextInput
                    label="Registry code"
                    required
                    {...form.getInputProps('registryCode')}
                  />
                  <TextInput
                    label="Registered office full address"
                    placeholder="Street, Postal code, City, Country"
                    required
                    {...form.getInputProps('registeredAddress')}
                  />
                  <TextInput
                    label="Country of incorporation (if different)"
                    {...form.getInputProps('countryOfIncorporation')}
                  />
                </>
              ) : (
                <>
                  <TextInput
                    label="Full name"
                    required
                    {...form.getInputProps('fullName')}
                  />
                  <TextInput
                    label="Passport number"
                    required
                    {...form.getInputProps('passportNumber')}
                  />
                  <DateInput
                    label="Date of birth"
                    required
                    clearable
                    {...form.getInputProps('dateOfBirth')}
                  />
                  <TextInput
                    label="Nationality"
                    required
                    {...form.getInputProps('nationality')}
                  />
                  <TextInput
                    label="Email"
                    type="email"
                    required
                    {...form.getInputProps('email')}
                  />
                  <TextInput
                    label="Registered residence full address"
                    placeholder="Street, Postal code, City, Country"
                    required
                    {...form.getInputProps('residenceAddress')}
                  />
                  <TextInput
                    label="Sole proprietorship reg. no. (if applicable)"
                    {...form.getInputProps('soleProprietorRegNo')}
                  />
                </>
              )}
            </>
          )}

          {requestType === 'contract_review' && (
            <>
              <Radio.Group
                label="Contract source"
                {...form.getInputProps('contractSource')}
              >
                <Group mt="xs">
                  <Radio value="Client Contract" label="Client Contract" />
                  <Radio value="AML Officer" label="AML Officer" />
                </Group>
              </Radio.Group>

              {form.values.contractSource === 'Client Contract' ? (
                <>
                  <TextInput
                    label="Opportunity number"
                    required
                    {...form.getInputProps('opportunityNumber')}
                  />
                  <TextInput
                    label="Exact name and type of product/package sold"
                    required
                    {...form.getInputProps('productName')}
                  />
                  <Textarea
                    label="Other Info"
                    autosize
                    minRows={2}
                    {...form.getInputProps('otherInfo')}
                  />
                </>
              ) : (
                <>
                  <TextInput
                    label="Hours Per Month"
                    {...form.getInputProps('hoursPerMonth')}
                  />
                  <TextInput
                    label="Commence Date"
                    {...form.getInputProps('commenceDate')}
                  />
                  <TextInput
                    label="Client Phone"
                    {...form.getInputProps('clientPhone')}
                  />
                  <TextInput
                    label="Designated Officer"
                    {...form.getInputProps('designatedOfficer')}
                  />
                </>
              )}
            </>
          )}

          <Text fz="sm" fw={500}>
            Attachments
          </Text>
          <Dropzone
            onDrop={(dropped) => setFiles((prev) => [...prev, ...dropped])}
            onReject={(rejections) => {
              rejections.forEach(({ file, errors }) => {
                notifications.show({
                  title: `Rejected: ${file.name}`,
                  message: errors.map((e) => e.message).join(', '),
                  color: 'red',
                });
              });
            }}
            maxSize={20 * 1024 ** 2}
          >
            <Group justify="center" gap="xl" style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <PiUploadLight
                  style={{
                    width: rem(32),
                    height: rem(32),
                    color: 'var(--mantine-color-blue-6)',
                  }}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <PiXLight
                  style={{
                    width: rem(32),
                    height: rem(32),
                    color: 'var(--mantine-color-red-6)',
                  }}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <VscFileMedia
                  style={{
                    width: rem(32),
                    height: rem(32),
                    color: 'var(--mantine-color-dimmed)',
                  }}
                />
              </Dropzone.Idle>
              <div>
                <Text inline>Drop files or click to attach</Text>
              </div>
            </Group>
          </Dropzone>
          {files.length > 0 && (
            <Stack gap={4}>
              {files.map((f, i) => (
                <Group key={i} gap="xs">
                  <Text fz="xs" c="dimmed" lineClamp={1}>
                    {f.name}
                  </Text>
                  <Text
                    fz="xs"
                    c="red"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, idx) => idx !== i))
                    }
                  >
                    remove
                  </Text>
                </Group>
              ))}
            </Stack>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              Create Request
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
